<template>
  <div>
    <Menu :options="menuOptions" v-if="menuOptions.length > 1">
      <template v-slot:activator="{ on }">
        <v-btn
          small
          icon
          color="blue-grey darken-1"
          v-on="on"
          @click.stop.prevent="openMenu"
        >
          <v-icon size="16">mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
    </Menu>
    <v-btn
      v-if="isRegularUser"
      icon
      color="blue-grey darken-1"
      @click.stop.prevent="showQRDialog"
    >
      <v-icon size="16">mdi-qrcode</v-icon>
    </v-btn>

    <QRCodeDialog
      data-testid="routineList-modal-qrModal"
      :routineDefinition="definition"
      :open="showShareQRDialog"
      @close="showShareQRDialog = false"
    />
    <CannotDeleteDialog
      :open="showCannotDeleteDialog"
      @cancel="showCannotDeleteDialog = false"
    />
    <DeleteConfirmationModal
      :show="showDeleteDialog"
      :isLoading="deletingRoutine"
      :title="$t('routines.delete_dialog_title')"
      :text="$t('routines.delete_routine_body')"
      @cancel="showDeleteDialog = false"
      @delete="deleteRoutine"
    />
  </div>
</template>

<script>
import QRCodeDialog from '../../components/Routines/QRCodeDialog.vue'
import CannotDeleteDialog from '../../components/Routines/CannotDeleteDialog.vue'
import DeleteConfirmationModal from '../../components/Modals/DeleteConfirmationModal.vue'
import Menu from '@/components/UI/Menu.vue'

import api from '@/api'
import mixin from '@/mixins/mixin'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin'

import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  name: 'ActionsWrapper',

  mixins: [mixin, trackerMixin, routineDefinitionPermissions],

  components: {
    Menu,
    QRCodeDialog,
    CannotDeleteDialog,
    DeleteConfirmationModal,
  },

  props: {
    definition: {
      type: Object,
      required: true,
    },

    organizationId: {
      type: String,
      required: false,
    },

    source: {
      type: String,
      required: false,
      default: 'card',
    },
  },

  data() {
    return {
      showCannotDeleteDialog: false,
      showDeleteDialog: false,
      showRoutineReminderModal: false,
      showShareQRDialog: false,
      deletingRoutine: false,
    }
  },

  computed: {
    ...mapGetters({
      account: 'account',
      accountOrganizationId: 'organizationId',
    }),

    menuOptions() {
      return _.compact([
        this.canEditRoutine && {
          icon: 'mdi-pencil-outline',
          testId: 'routineList-button-edit',
          tourId: 'routineList-button-edit',
          id: 'edit',
          title: this.$t('common.button.edit'),
          action: () => {
            this.trackRoutineViewNavigate({
              option: 'edit',
              action: 'menu',
              source: this.source,
              id: this.definition.id,
            })
            this.navigateTo({
              name: 'editRoutine',
              params: { routineDefinitionId: this.definition.id },
            })
          },
        },
        this.canCreateRoutine && {
          title: this.$t('common.button.duplicate'),
          testId: 'routineList-button-duplicate',
          tourId: 'routineList-button-duplicate',
          icon: 'mdi-content-copy',
          action: () => {
            this.trackRoutineViewNavigate({
              option: 'duplicate',
              action: 'menu',
              source: this.source,
              id: this.definition.id,
            })
            this.navigateTo({
              name: 'createRoutineDefinition',
              params: {
                routineDefinitionId: this.definition.id,
                duplicate: true,
              },
            })
          },
        },
        {
          title: this.$t('routines.qr.qr_code'),
          testId: 'listMenu-button-qrCode',
          tourId: 'routineList-button-qrCode',
          icon: 'mdi-qrcode',
          action: () => {
            this.trackRoutineViewNavigate({
              action: 'menu',
              option: 'showQRCode',
              source: this.source,
              id: this.definition.id,
            })
            this.showQRDialog()
          },
        },
        this.canDeleteRoutine && {
          title: this.$t('common.button.delete'),
          testId: 'routineList-button-delete',
          tourId: 'routineList-button-delete',
          icon: 'mdi-delete-outline',
          action: async () => {
            this.trackRoutineViewNavigate({
              action: 'menu',
              option: 'delete',
              source: this.source,
              id: this.definition.id,
            })
            this.handleDeleteRoutine()
          },
        },
      ])
    },

    canCreateRoutine() {
      return this.getRoutineDefinitionPermissions({}).create
    },

    canDeleteRoutine() {
      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.definition,
      }).delete
    },

    canEditRoutine() {
      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.definition,
      }).edit
    },

    isOrganizationAdminOrReverAdmin() {
      return this.account.isAdmin || this.account.isOrganizationAdmin
    },

    isRoutineManagerOrSiteAdmin() {
      return this.account.isSiteAdmin || this.account.isRoutineManager
    },

    isRegularUser() {
      return (
        !this.isOrganizationAdminOrReverAdmin &&
        !this.isRoutineManagerOrSiteAdmin
      )
    },
  },

  methods: {
    async deleteRoutine() {
      try {
        this.deletingRoutine = true
        await api.routine.delete(this.organizationId, this.definition.id)
        this.$emit('getRoutines')
        this.showSuccess(this.$t('routines.routine_deleted_success'))
        this.showDeleteDialog = false
        this.trackRoutineDelete({
          action: 'delete',
          id: this.definition.id,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.deletingRoutine = false
      }
    },

    cancelDelete() {
      this.trackRoutineDelete({
        action: 'cancelDelete',
      })
    },

    async handleDeleteRoutine() {
      const routineExecutions = await this.getRoutineExecutions()
      if (routineExecutions.length) {
        this.showCannotDeleteDialog = true
      } else {
        this.showDeleteDialog = true
      }
    },

    async getRoutineExecutions() {
      try {
        const response = await api.routine.getRoutineExecutions(
          this.organizationId,
          {
            filters: {
              routineDefinition: this.definition.id,
            },
            limit: 1,
            page: 1,
          }
        )
        return response?.data?.results ?? []
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    showQRDialog() {
      this.trackRoutineQrView({
        source: this.source,
      })
      this.showShareQRDialog = true
    },

    openMenu() {
      this.trackRoutineViewNavigate({
        action: 'openMenu',
        source: this.source,
        id: this.definition.id,
      })
    },
  },
}
</script>
